/**
 * Broadcasts service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. Return-shape note: `get`/`patch` return `response.data`;
 * `post`/`remove` return `{ data, status }`.
 *
 * Surface is v1_10_0 for broadcasts and v1_11_0 for the audience-filter routes
 * at the bottom. The pre-v1_10_0 functions (`getAnalytics`, `updateBroadcast`,
 * `deleteBroadcast`, `estimateAudience`) are gone — their last callers went
 * with the old composer, list and analytics pages.
 */
import { buildQuery } from "./_query";
import { wrapCommsError } from "./_error";
import { presignUpload } from "./messagesService";

/**
 * The list screen's "All" tab/chip is the ABSENCE of a filter, not a value the
 * API understands. `CHANNEL_TABS`/`STATUS_FILTERS` in `broadcastEnums.js` use
 * the id `"all"` for that; sending `channel=all` to the backend would filter on
 * a channel that does not exist and return nothing. Map it away here — the
 * service is the last place before the wire, so no caller has to remember.
 *
 * The enums are deliberately not imported: a service must not depend on a page
 * module, and the contract here is the string, not the constant.
 *
 * @param {string|undefined} value - A filter value, possibly `"all"`.
 * @returns {string|undefined} `undefined` when the value is the sentinel.
 */
const dropAllSentinel = (value) => (value === "all" ? undefined : value);

/**
 * Upload scope for broadcast images.
 *
 * Deliberately `"broadcast"`, which the dev backend does not yet accept — see
 * the note on `presignBroadcastImage` below. Named rather than inlined so the
 * value and its reasoning stay together.
 */
const BROADCAST_UPLOAD_SCOPE = "broadcast";

/**
 * List broadcasts.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ channel, status, dateFrom, dateTo, mine,
 *   limit, offset }`. `channel` and `status` accept the `"all"` sentinel,
 *   which omits the param entirely.
 * @returns {Promise<{ broadcasts: Array<Object>, total: number }>}
 */
export const listBroadcasts = async (
  get,
  { channel, status, dateFrom, dateTo, mine, limit, offset } = {}
) => {
  try {
    const query = buildQuery({
      channel: dropAllSentinel(channel),
      status: dropAllSentinel(status),
      date_from: dateFrom,
      date_to: dateTo,
      mine,
      limit,
      offset,
    });
    return await get(`/api/v1/broadcasts${query}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching broadcasts");
  }
};

/**
 * Headline counters for the list screen (sent, scheduled, reach, engagement)
 * over a rolling window.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ channel, days }`. `channel` accepts the
 *   `"all"` sentinel, which omits the param entirely.
 * @returns {Promise<Object>} Overview payload.
 */
export const getOverview = async (get, { channel, days } = {}) => {
  try {
    const query = buildQuery({ channel: dropAllSentinel(channel), days });
    return await get(`/api/v1/broadcasts/overview${query}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching broadcast overview");
  }
};

/**
 * Create a broadcast (send now, schedule, or save a draft — the payload says
 * which).
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - Broadcast payload, built by `broadcastPayload.js`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const createBroadcast = async (post, payload) => {
  try {
    return await post(`/api/v1/broadcasts`, payload);
  } catch (err) {
    throw wrapCommsError(err, "Error creating broadcast");
  }
};

/**
 * Estimate the reachable audience for a draft, per channel.
 *
 * Called on a debounce while the composer's audience is being edited, so the
 * caller is expected to tolerate a rejection without blocking the send.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} payload - Estimate payload, built by `broadcastPayload.js`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const estimateBroadcast = async (post, payload) => {
  try {
    return await post(`/api/v1/broadcasts/estimate`, payload);
  } catch (err) {
    throw wrapCommsError(err, "Error estimating audience");
  }
};

/**
 * Delivery funnel for one broadcast (queued → sent → delivered → opened →
 * clicked, plus suppressed/bounced on email).
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} id - Broadcast id.
 * @returns {Promise<Object>} Funnel payload.
 */
export const getFunnel = async (get, id) => {
  try {
    return await get(`/api/v1/broadcasts/${id}/funnel`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching broadcast funnel");
  }
};

/**
 * Per-recipient rows for one broadcast, filtered by delivery state or
 * engagement and optionally searched.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} id - Broadcast id.
 * @param {Object} [options] - `{ state, engagement, q, limit, offset }`.
 * @returns {Promise<{ recipients: Array<Object>, total: number }>}
 */
export const listRecipients = async (
  get,
  id,
  { state, engagement, q, limit, offset } = {}
) => {
  try {
    const query = buildQuery({ state, engagement, q, limit, offset });
    return await get(`/api/v1/broadcasts/${id}/recipients${query}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching broadcast recipients");
  }
};

/**
 * Retract a broadcast. Stops anything still queued; already-delivered
 * notifications cannot be recalled.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} id - Broadcast id.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const retractBroadcast = async (post, id) => {
  try {
    return await post(`/api/v1/broadcasts/${id}/retract`);
  } catch (err) {
    throw wrapCommsError(err, "Error retracting broadcast");
  }
};

/**
 * Request a presigned upload for a broadcast hero image.
 *
 * Shares the generic `/uploads/presign` route with chat attachments — only the
 * `scope` differs, so this delegates to `presignUpload` rather than restating
 * the call.
 *
 * SCOPE — probed against the dev backend on 2026-08-09:
 *
 *   scope: "broadcast"  →  400  "scope must be one of ('chat', 'ai', 'support')"
 *
 * We send `"broadcast"` anyway, by decision. The frontend states the scope it
 * actually means, and the backend adds it; sending `"chat"` instead would work
 * today at the cost of filing every broadcast image into the chat container,
 * where chat's retention and access policy would silently govern it and no
 * future migration could tell the two apart.
 *
 * Until the scope lands, this 400 is expected. It is not a crash and not a bug
 * report: `useBroadcastImage` classifies any 4xx from presign as `unsupported`,
 * the dropzone swaps to "Image upload isn't available on this server yet — you
 * can still send this broadcast without an image", and every other field on
 * the composer keeps working. That path is built and tested precisely so this
 * answer costs nothing.
 *
 * BACKEND, WHEN ADDING THE SCOPE — the harder half of the problem:
 * `data_url` is not publicly fetchable. The container is private and a direct
 * GET answers 409, so FCM cannot render a push image and an email client
 * cannot load an <img> — neither can send an Authorization header. Whatever
 * ms-agents stores as `image_url` must be resolvable WITHOUT a bearer token at
 * delivery time. A `"broadcast"` scope is the natural place to hang that
 * public-read policy, which is the second reason not to reuse `"chat"`.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} file - `{ fileName, contentType, fileSize }`.
 * @returns {Promise<{ data: { upload_url, data_url, blob_path,
 *   content_type_bucket, expires_at }, status: number }>}
 */
export const presignBroadcastImage = async (
  post,
  { fileName, contentType, fileSize }
) =>
  presignUpload(post, {
    file_name: fileName,
    content_type: contentType,
    file_size: fileSize,
    scope: BROADCAST_UPLOAD_SCOPE,
  });

/* ── Audience filters (v1_11_0) ───────────────────────────────────────── */

/** Every filters route hangs off this prefix. */
const FILTERS_PATH = "/api/v1/broadcasts/filters";

/**
 * WHERE THE TENANT ID GOES — the asymmetry, stated once, here.
 *
 * Five of the six filter routes take the company id as a QUERY PARAMETER:
 *
 *   GET  /filters?company_id=            POST /filters?company_id=
 *   GET  /filters/{id}/count?company_id= GET  /filters/fields?company_id=
 *   GET  /filters/presets?company_id=    POST /filters/install-defaults?company_id=
 *
 * The sixth, `POST /filters/preview`, takes it in the BODY alongside `rules`.
 *
 * This is the kind of difference that does not announce itself. A company id
 * sent in the wrong half of the request is not a 400 — the parameter is simply
 * absent from where the handler looks, the query runs unscoped or empty, and
 * the answer comes back 200 with a plausible-looking zero in it. The composer
 * then tells an author their filter reaches nobody, which is indistinguishable
 * from a filter that genuinely does. The same shape of bug once made every
 * audience estimate read zero for weeks.
 *
 * So this service is the single place the id is placed, and `previewFilter`
 * takes `{ companyId, rules }` and assembles the body itself rather than
 * accepting a pre-built one — a caller who never spells the wire name cannot
 * spell it in the wrong place. That is also why `broadcastsService.js` is the
 * one non-owner module allowed to name `company_id` at all.
 */

/**
 * List this company's saved audience filters.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ companyId }`.
 * @returns {Promise<{ filters: Array<Object>, total: number }>}
 */
export const listFilters = async (get, { companyId } = {}) => {
  try {
    return await get(`${FILTERS_PATH}${buildQuery({ company_id: companyId })}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching audience filters");
  }
};

/**
 * Create a saved filter.
 *
 * The body is `{ name, rules }` and arrives pre-built — an empty `rules` list
 * is a 422 by design, not a filter that matches everyone, and deciding that is
 * the caller's job (see `MIN_FILTER_RULES` in `broadcastEnums.js`).
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} [options] - `{ companyId }`.
 * @param {Object} body - `{ name, rules }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const createFilter = async (post, { companyId } = {}, body) => {
  try {
    return await post(`${FILTERS_PATH}${buildQuery({ company_id: companyId })}`, body);
  } catch (err) {
    throw wrapCommsError(err, "Error creating audience filter");
  }
};

/**
 * Update a saved filter.
 *
 * @param {Function} patch - ms-agents PATCH verb.
 * @param {string} id - Filter id.
 * @param {Object} body - Partial `{ name, rules, is_active }`.
 * @returns {Promise<Object>}
 */
export const updateFilter = async (patch, id, body) => {
  try {
    return await patch(`${FILTERS_PATH}/${id}`, body);
  } catch (err) {
    throw wrapCommsError(err, "Error updating audience filter");
  }
};

/**
 * Delete a saved filter.
 *
 * @param {Function} remove - ms-agents DELETE verb.
 * @param {string} id - Filter id.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const deleteFilter = async (remove, id) => {
  try {
    return await remove(`${FILTERS_PATH}/${id}`);
  } catch (err) {
    throw wrapCommsError(err, "Error deleting audience filter");
  }
};

/**
 * Preview an UNSAVED rule list: how many people it matches and how many of
 * those each channel can actually reach.
 *
 * THE ONE ROUTE THAT TAKES THE COMPANY ID IN THE BODY. See the note above.
 *
 * Called on a debounce while rules are being edited, so the caller is expected
 * to tolerate a rejection without blocking anything.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} options - `{ companyId, rules }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const previewFilter = async (post, { companyId, rules } = {}) => {
  try {
    return await post(`${FILTERS_PATH}/preview`, { company_id: companyId, rules });
  } catch (err) {
    throw wrapCommsError(err, "Error previewing audience filter");
  }
};

/**
 * The registry of filterable fields.
 *
 * Each entry carries an `available` flag. A field can be wired end-to-end and
 * still be unavailable because the table behind it is empty — the builder must
 * read that flag rather than assume everything listed is usable.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ companyId }`.
 * @returns {Promise<{ fields: Array<Object> }>}
 */
export const listFilterFields = async (get, { companyId } = {}) => {
  try {
    return await get(`${FILTERS_PATH}/fields${buildQuery({ company_id: companyId })}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching filter fields");
  }
};

/**
 * The preset catalogue.
 *
 * `withCounts` makes the server count every preset, which is real work — so a
 * falsy value omits the param entirely rather than sending `with_counts=false`
 * and inviting the backend to interpret the string "false".
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ companyId, withCounts }`.
 * @returns {Promise<{ presets: Array<Object> }>}
 */
export const listFilterPresets = async (get, { companyId, withCounts } = {}) => {
  try {
    const query = buildQuery({
      company_id: companyId,
      with_counts: withCounts ? true : undefined,
    });
    return await get(`${FILTERS_PATH}/presets${query}`);
  } catch (err) {
    throw wrapCommsError(err, "Error fetching filter presets");
  }
};

/**
 * Install the default filter set for this company.
 *
 * Idempotent by design: the response splits what it created from what was
 * already there, so a second click is safe and says so.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {Object} [options] - `{ companyId }`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const installDefaultFilters = async (post, { companyId } = {}) => {
  try {
    return await post(
      `${FILTERS_PATH}/install-defaults${buildQuery({ company_id: companyId })}`
    );
  } catch (err) {
    throw wrapCommsError(err, "Error installing default filters");
  }
};
